import { FontIcon, Icon, Spinner, SpinnerSize, Text, TooltipHost, TooltipOverflowMode, mergeStyleSets } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { dropZoneStyleSets } from "../../../../../config/controlStyles";
import { IDocumentLibrarySharepoint } from "../../../../../interfaces/IDocumentLibrarySharepoint";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { fontIconStyles } from "../../../../../styles/iconStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { ISharepointDocumentLocation } from "../../../../../interfaces/ISharepointDocument";
import { buildWorkingDirectoryFromTree, findWorkingDirectories, treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import generalModule from "../../../../../../../utilities/GeneralModule";
import { RootState } from "../../../redux/storeMain";
import { logger } from "../../../../../../../utilities/logger";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";

const classNames = mergeStyleSets({
    svgIcon: {
        fontSize: 18
    }
});

interface ISharePointDocumentLibraryItemProps {
    item?: IDocumentLibrarySharepoint;
    treeItem?: ISharepointDocumentLocation;
    isMobile: boolean;
    isValidDropZone: boolean;
    hasFileSelected: boolean;
    allowScroll?: boolean;
    getTreeLocations?: () => any[];
    onTreeItemClick?: (event: React.SyntheticEvent, treeItem: any) => Promise<void>;
    addSharepointFolderDirectory?: (item: IDocumentLibrarySharepoint) => void;
    handleSaveClick: (item: any, e: any) => Promise<void>;
    openFolderOptions?: (documentLibrary: IDocumentLibrarySharepoint) => void;
    openFolderOptionsTree?: (e: any) => void;
    onTreeExpand?: (isRefresh: boolean) => void;
}

export const SharePointDocumentLibraryItem = ({ item, treeItem, isMobile, isValidDropZone, hasFileSelected, allowScroll, getTreeLocations, onTreeItemClick, addSharepointFolderDirectory, handleSaveClick, openFolderOptions, openFolderOptionsTree, onTreeExpand }: ISharePointDocumentLibraryItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.user);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [treeItem]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        try {
            if (DragAndDropHelper.validateDroppedFiles(event)) {
                dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event, treeItem)));
                handleSaveClick(item ? item : treeItem, event);
            }
            else if (DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
                const newWorkingDirectories = findWorkingDirectories(getTreeLocations(), treeItem);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const siteUrl = currDirectory.webUrl

                const origin = new URL(currDirectory.webUrl).origin;
                const destinationServerRelPath = `${currDirectory.webUrl.replace(origin, "")}/${currDirectory.directory}`;

                DragAndDropHelper.destinationDirectory = currDirectory;
                DragAndDropHelper.handleGridMove(dispatch, siteUrl, directories, false, destinationServerRelPath);
            }
        } catch (error) {
            logger(error, "SharePointDocumentLibraryItem.tsx (handleDrop)");
        }
    }, [item, treeItem, handleSaveClick]);

    const className = React.useMemo(() => {
        let className: string = "list-item";
        if (treeItem) {
            className += ` ${"tree-item"}`;
            if (treeItem.isSelected) {
                className += ` ${"tree-item-selected"}`;
            }
        }

        if (isDraggedOver) {
            className += ` dropzone ${dropZoneStyleSets.dropzoneActive}`;
        }
        else if (isValidDropZone) {
            className += ` dropzone`;
        }
        return className;
    }, [isValidDropZone, treeItem, isDraggedOver]);

    const showChevron = React.useMemo((): boolean => {
        return treeItem && !treeItem.isList && (((treeItem.canExpand && !treeItem.isLoading) || (treeItem.isLoading && treeItem.foldersNextHref)) &&
            !(treeItem.loaded && treeItem.folders?.length === 0));
    }, [treeItem]);

    React.useEffect(() => {
        let debounceTimer;
        if (treeItem && isValidDropZone && isDraggedOver && showChevron && onTreeExpand && !treeItem.expanded && !treeItem.isLoading) {
            debounceTimer = setTimeout(() => {
                onTreeExpand(false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [treeItem, isValidDropZone, isDraggedOver, showChevron, onTreeExpand]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    return (
        <div
            className={className}
            style={folderStyles}
            onDragEnter={isValidDropZone ? dragEnter : undefined}
            onDragOver={isValidDropZone ? dragOver : undefined}
            onDragLeave={isValidDropZone ? dragLeave : undefined}
            onDrop={isValidDropZone ? handleDrop : undefined}
            onClick={(e) => {
                e.stopPropagation();
                if (onTreeItemClick) {
                    onTreeItemClick(e, treeItem);
                }
                else if (addSharepointFolderDirectory) {
                    addSharepointFolderDirectory(item);
                }
            }}
        >
            <div className={!allowScroll ? "list-item-link" : "list-item-link-scrollable"}>
                {treeItem &&
                    <div
                        style={treeViewItemStyle(treeItem)}
                        onClick={(e: any) => {
                            if (showChevron) {
                                e.stopPropagation();
                                onTreeExpand(false);
                            }
                        }}>
                        {treeItem.isLoading && !treeItem.foldersNextHref && <Spinner size={SpinnerSize.xSmall} />}
                        {showChevron && <FontIcon iconName={treeItem.expanded ? "ChevronDown" : "ChevronRight"} />}
                    </div>
                }

                <div className="list-item-icon">
                    {!treeItem?.isList
                        ? <FontIcon iconName="DocLibrary" className={item ? fontIconStyles : classNames.svgIcon} />
                        : <Icon styles={{ root: { marginLeft: 12 } }}
                            {...getFileTypeIconProps({
                                type: FileIconType.list,
                                size: 20,
                                imageFileType: "svg",
                            })}
                        />}
                </div>
                <Text className="list-item-name">
                    <TooltipHost content={item?.name || treeItem?.name} overflowMode={TooltipOverflowMode.Parent}>
                        {item?.name || treeItem?.name}
                    </TooltipHost>
                </Text>
            </div>
            {treeItem && !treeItem.isLoading && treeItem.loaded && <TooltipHost content={languageStrings.Refresh}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    <FontIcon
                        iconName="Refresh"
                        className="refresh-icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onTreeExpand(true);
                        }}
                    />
                </div>
            </TooltipHost>}
            {hasFileSelected && isValidDropZone && !generalModule.isInConentMangerMode() && (
                <TooltipHost content={`${languageStrings.SaveTo} ${item ? item.name : treeItem.name}`}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        <FontIcon
                            iconName="Save"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                handleSaveClick(item ? item : treeItem, e);
                            }}
                        />
                    </div>
                </TooltipHost>
            )}
            <TooltipHost content={languageStrings.MoreActions}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    <FontIcon
                        iconName="More"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item) {
                                openFolderOptions(item);
                            } else {
                                openFolderOptionsTree(e);
                            }
                        }}
                    />
                </div>
            </TooltipHost>
        </div>
    )
};